'use client';

import type { GroupColumnDef } from '@tanstack/react-table';
import { usePathname, useSearchParams } from 'next/navigation';
import TableTooltip from '@/components/Table/components/TableTooltip';
import { calcMinSize } from '@/components/Table/utils/calcColumnSize';
import { InternalLinkCell } from '@/components/Table/utils/InternalLinkCell';
import { Skeleton } from '@/components/ui/skeleton';
import type { AvailableSeries } from '@/types/series';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';
import SortButton from '../../components/SortButton';
import type { DynamicTableRow } from '.';

const TITLE_SERIE: SeriesDefinition = {
  decimals: 0,
  delta: 'relative',
  description: '',
  private: false,
  shortname: 'Namn',
  slug: 'name' as AvailableSeries,
  title: 'Summering',
  unit: '',
  isSelected: true,
  isLink: true,
};

export const titleColumn = (rows?: DynamicTableRow[]): GroupColumnDef<DynamicTableRow> => ({
  id: TITLE_SERIE.slug,
  meta: {
    title: TITLE_SERIE.title,
    description: TITLE_SERIE.description,
    isDraggable: false,
    draggableId: TITLE_SERIE.slug,
    isVisible: false,
  },
  header: '',
  columns: [
    {
      id: `_${TITLE_SERIE.slug}`,
      accessorFn: (row) => row.title?.[0]?.name,
      minSize: rows ? calcMinSize('title[0].name', rows, TITLE_SERIE.title) : 170,
      maxSize: rows ? calcMinSize('title[0].name', rows, TITLE_SERIE.title) : 170,
      meta: {
        isDraggable: false,
      },
      header: (ctx) => {
        const { column } = ctx;
        const isSorted = column.getIsSorted();
        const canSort = column.getCanSort();
        return (
          <div className='pointer-events-auto flex h-10 w-full flex-col gap-0.5 text-left'>
            <div className='flex items-center gap-1'>
              {TITLE_SERIE.description ? (
                <TableTooltip
                  description={TITLE_SERIE.description}
                  align={'start'}
                >
                  {TITLE_SERIE.shortname}
                </TableTooltip>
              ) : (
                <span>{TITLE_SERIE.shortname}</span>
              )}
              {canSort && (
                <SortButton
                  column={column}
                  isSorted={isSorted}
                  className='text-xs'
                />
              )}
            </div>
            <span className='text-xs font-normal'>Summering</span>
          </div>
        );
      },
      cell: ({ row }) => {
        //This makes the cell only be allowed to be rendered on the client, could make this attempt on the server as well since we have the original url from the server.
        const searchParams = useSearchParams();
        const pathname = usePathname();
        if (!rows) return <Skeleton className='h-2 w-8' />;
        if (!row.original.title) return <div>-</div>;
        if (row.original.title[0].disableLink) {
          return <span className='font-display block truncate'>{row.original.title[0].name}</span>;
        }
        return (
          <InternalLinkCell
            links={[
              {
                id: `${row.original.title[0].id}?${searchParams.toString()}`,
                name: row.original.title[0].name,
                linkPath: pathname,
              },
            ]}
          />
        );
      },
    },
  ],
});
